import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Sparkles } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import type { BadgeTone } from '../atoms/Badge/Badge.types';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { EmptyState } from '../atoms/EmptyState';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { AiLimitErrorMessage } from '../aiUsage/AiLimitNotice';
import { errorCode, errorReference } from '../lighting/LightEvaluationCard';
import { FlowMapPhoto } from './FlowMapPhoto';
import { getAiLimitErrorKind } from '../../services/aiUsageService';
import { invalidateAiUsage, useAiUsage } from '../../hooks/useAiUsage';
import { LIGHT_GROUPS, type LightGroup } from '../../services/lightSurveyService';
import { formatRange } from '../../services/flowSurveyService';
import { getPlacement, getPlacementAdvice, placementKey, type AdviceVerdict, type PlacementCandidate, type PlacementQuery } from '../../services/placementService';
import '../lighting/Lighting.css';
import './FlowMap.css';

const VERDICT_TONE: Readonly<Record<AdviceVerdict, BadgeTone>> = { recommended: 'success', possible: 'warning', avoid: 'danger' };

export interface PlacementAssistantProps {
  aquariumId: string;
}

/**
 * Where should a new coral go? The care group (chosen, or recognised from the
 * name) is matched against the light map and the flow map together: the spots
 * where both fit, best first, on the photo and as a list. On request the AI
 * chooses among them with the photo in view — it cannot add a spot.
 */
export function PlacementAssistant({ aquariumId }: PlacementAssistantProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { exceeded: aiCreditsUsedUp } = useAiUsage();
  const [name, setName] = useState('');
  const [group, setGroup] = useState<LightGroup | ''>('');
  const [asked, setAsked] = useState<PlacementQuery | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  const query = useQuery({
    queryKey: placementKey(aquariumId, asked ?? {}),
    queryFn: () => getPlacement(aquariumId, asked!),
    enabled: Boolean(aquariumId && asked),
    retry: false,
  });
  const advice = useMutation({
    mutationFn: () => getPlacementAdvice(aquariumId, asked!),
    onSuccess: () => invalidateAiUsage(queryClient),
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!group && name.trim() === '') {
      setError(String(t('flow.placement.needInput')));
      return;
    }
    setError(undefined);
    advice.reset();
    setAsked({ ...(group ? { group } : {}), ...(name.trim() ? { name: name.trim() } : {}) });
  };

  const placement = query.data;
  let result = null;
  if (asked && query.isPending) {
    result = <LiveRegion busy><Text color="muted">{t('flow.placement.loading')}</Text></LiveRegion>;
  } else if (asked && query.isError) {
    result =
      errorCode(query.error) === 'UNKNOWN_GROUP' ? (
        <Alert tone="info" compact live="polite">{t('flow.placement.unknownGroup')}</Alert>
      ) : (
        <LoadErrorState title={t('flow.placement.loadError')} onRetry={() => void query.refetch()} retryLabel={t('flow.retry')} titleLevel={3} />
      );
  } else if (placement && placement.mode === 'no_data') {
    result = (
      <EmptyState
        icon={<MapPin aria-hidden="true" />}
        headline={t('flow.placement.noMapsTitle')}
        description={t('flow.placement.noMaps')}
        headingLevel={3}
        actionLabel={t('flow.placement.openLighting')}
        actionVariant="secondary"
        onAction={() => navigate(`/aquariums/${encodeURIComponent(aquariumId)}/lighting`)}
      />
    );
  } else if (placement) {
    const byRank = new Map((advice.data?.advice.picks ?? []).map((p) => [p.rank, p]));
    // "light A" / "flow B" from the API → the translated map name and the point label.
    const nearLabel = (n: string): string => {
      const [map, label] = n.split(' ');
      return String(t(map === 'light' ? 'flow.placement.lightPoint' : 'flow.placement.flowPoint', { label }));
    };
    const where = (c: PlacementCandidate): string =>
      c.kind === 'zone' ? String(t(`flow.evaluation.bands.${c.zone}`)) : c.near.length ? String(t('flow.placement.near', { points: c.near.map(nearLabel).join(', ') })) : String(t('flow.placement.position', { x: Math.round((c.x ?? 0) * 100), y: Math.round((c.y ?? 0) * 100) }));
    result = (
      <Stack gap="4" data-testid="placement-result">
        <Text size="sm" color="secondary">
          {t('flow.placement.needs', {
            group: t(`lighting.groups.${placement.group}`),
            par: placement.need.minPar !== null ? `${placement.need.minPar}–${placement.need.maxPar}` : '–',
            flow: formatRange(placement.need.peakMin, placement.need.peakMax),
          })}
        </Text>
        {placement.mode !== 'same_photo' && <Alert tone="info" compact live="off">{t(`flow.placement.mode.${placement.mode}`)}</Alert>}
        {placement.candidates.length === 0 ? (
          <Text>{t('flow.placement.none')}</Text>
        ) : (
          <>
            {placement.candidates.some((c) => c.x !== null) && (
              <FlowMapPhoto
                imageUrl={placement.imageUrl}
                aspect={placement.imageWidth && placement.imageHeight ? placement.imageWidth / placement.imageHeight : 4 / 3}
                pumps={[]}
                points={[]}
                corals={placement.candidates.filter((c) => c.x !== null).map((c) => ({ id: String(c.rank), number: c.rank, x: c.x!, y: c.y!, status: c.fitsLight && c.fitsFlow ? 'ok' : 'uncertain' }))}
                grid={[]}
                showGrid={false}
                highlightIds={null}
                pending={null}
              />
            )}
            <ol className="lighting__list" data-testid="placement-candidates">
              {placement.candidates.map((c) => {
                const pick = byRank.get(c.rank);
                return (
                  <li key={c.rank} className="lighting__evaluation-item">
                    <Inline gap="2" align="center">
                      <Text weight="semibold">{t('flow.placement.candidate', { rank: c.rank, where: where(c) })}</Text>
                      <Badge tone={c.fitsLight ? 'success' : 'warning'}>{t(c.fitsLight ? 'flow.placement.lightFits' : 'flow.placement.lightOff')}</Badge>
                      <Badge tone={c.fitsFlow ? 'success' : 'warning'}>{t(c.fitsFlow ? 'flow.placement.flowFits' : 'flow.placement.flowOff')}</Badge>
                      {pick && <Badge tone={VERDICT_TONE[pick.verdict]}>{t(`flow.placement.verdict.${pick.verdict}`)}</Badge>}
                    </Inline>
                    <Text size="sm" color="secondary">
                      {[
                        c.par !== null ? String(t('flow.placement.par', { par: c.par })) : null,
                        c.flowMin !== null && c.flowMax !== null ? String(t('flow.placement.flow', { range: formatRange(c.flowMin, c.flowMax) })) : null,
                        String(t('flow.placement.score', { score: c.score })),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                    {c.occupiedBy && <Text size="sm">{t('flow.placement.occupied', { name: c.occupiedBy })}</Text>}
                    {pick?.reason && <Text size="sm" lang={advice.data?.advice.language}>{pick.reason}</Text>}
                  </li>
                );
              })}
            </ol>
            <Text size="sm" color="muted">
              {t('flow.placement.basedOn', { light: placement.lightSurvey?.title ?? '–', flow: placement.flowSurvey?.title ?? '–' })}
              {placement.lightSurvey && (
                <>
                  {' · '}
                  <Link className="lighting__back-link" to={`/aquariums/${encodeURIComponent(aquariumId)}/lighting/${encodeURIComponent(placement.lightSurvey.id)}`}>{t('flow.placement.openLightMap')}</Link>
                </>
              )}
            </Text>
            {advice.data ? (
              <Card>
                <Stack gap="3" data-testid="placement-advice">
                  <Inline gap="2" align="center">
                    <span className="lighting__evaluation-icon" aria-hidden="true"><Sparkles size={20} /></span>
                    <Heading level={3}>{t('flow.placement.adviceTitle')}</Heading>
                  </Inline>
                  <Text lang={advice.data.advice.language}>{advice.data.advice.summary}</Text>
                  {advice.data.advice.cautions.length > 0 && (
                    <ul className="lighting__tips" lang={advice.data.advice.language}>
                      {advice.data.advice.cautions.map((c) => <li key={c}><Text size="sm">{c}</Text></li>)}
                    </ul>
                  )}
                  {advice.data.advice.acclimation && <Text size="sm" lang={advice.data.advice.language}>{t('flow.placement.acclimation', { text: advice.data.advice.acclimation })}</Text>}
                  <Text variant="caption" color="muted">{t('flow.placement.disclaimer')}</Text>
                </Stack>
              </Card>
            ) : (
              <Inline gap="2">
                <Button type="button" variant="secondary" isLoading={advice.isPending} disabled={aiCreditsUsedUp} onClick={() => advice.mutate()}>
                  <Sparkles size={16} aria-hidden="true" />
                  {t('flow.placement.askAi')}
                </Button>
              </Inline>
            )}
            {advice.isPending && <LiveRegion busy><Text color="muted">{t('flow.placement.asking')}</Text></LiveRegion>}
            {advice.isError &&
              (getAiLimitErrorKind(advice.error) ? (
                <AiLimitErrorMessage error={advice.error} suppressExceeded={aiCreditsUsedUp} />
              ) : (
                <Alert compact>
                  {t('flow.placement.adviceError')}
                  {errorReference(advice.error) && (
                    <Text as="span" size="sm" className="lighting__error-reference">{t('lighting.evaluation.errorReference', { reference: errorReference(advice.error) })}</Text>
                  )}
                </Alert>
              ))}
          </>
        )}
      </Stack>
    );
  }

  return (
    <Card>
      <Stack gap="4">
        <form onSubmit={submit} noValidate>
          <Stack gap="3">
            <Inline gap="3" align="start">
              <Input id="placement-name" label={t('flow.placement.name')} placeholder={t('flow.editor.coralNamePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} error={error} />
              <Select
                id="placement-group"
                label={t('flow.editor.group')}
                value={group}
                options={[{ value: '', label: String(t('lighting.groups.auto')) }, ...LIGHT_GROUPS.map((g) => ({ value: g, label: String(t(`lighting.groups.${g}`)) }))]}
                onChange={(e) => setGroup(e.target.value as LightGroup | '')}
              />
            </Inline>
            <Inline gap="2">
              <Button type="submit" variant="secondary" isLoading={Boolean(asked) && query.isFetching}>{t('flow.placement.find')}</Button>
            </Inline>
          </Stack>
        </form>
        {result}
      </Stack>
    </Card>
  );
}

export default PlacementAssistant;
